// src/pages/Settings.jsx
import React, { useEffect, useState, useCallback, useContext } from "react";
import axios from "axios";
import config from "../config/config";
import { Card, CardHeader, CardTitle, CardBody } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { SvgIcon } from "@progress/kendo-react-common";
import { saveIcon, trashIcon } from "@progress/kendo-svg-icons";
import { orderBy } from "@progress/kendo-data-query";
import { AppContext } from "../AppContext";

/* Hilfs-Funktionen ------------------------------------------------------- */
const ensureArray = (val, fb = []) => (Array.isArray(val) ? val : fb);
const dictToTaskArray = (o) =>
  Object.entries(o).map(([k, v], i) => ({
    Position:     parseInt(k.replace(/\D/g, ""), 10) || i + 1,
    Beschreibung: typeof v === "string" ? v : v?.Beschreibung || "",
    DetailText:   typeof v === "string" ? "" : v?.DetailText   || "",
    Einheit:      typeof v === "string" ? "" : v?.Einheit      || "",
  }));
const dictToUnitArray = (o) =>
  Object.entries(o).map(([k, v], i) => ({
    Position: parseInt(k.replace(/\D/g, ""), 10) || i + 1,
    Einheit:  v,
  }));

/* Edit-Cells ------------------------------------------------------------- */
const DescriptionDetailCell = React.memo(
  ({ dataItem, dataIndex, updateRow }) => {
    const [desc,   setDesc]   = useState(dataItem.Beschreibung || "");
    const [detail, setDetail] = useState(dataItem.DetailText   || "");

    useEffect(() => setDesc(dataItem.Beschreibung || ""), [dataItem.Beschreibung]);
    useEffect(() => setDetail(dataItem.DetailText  || ""), [dataItem.DetailText ]);

    return (
      <td style={{ padding: 0 }}>
        <Input
          value={desc}
          placeholder="Beschreibung"
          onChange={(e) => setDesc(e.value)}
          onBlur={() => updateRow(dataIndex, { Beschreibung: desc })}
          style={{ width: "100%", borderRadius: 0, borderBottom: "none", marginTop: "15px" }}
        />
        <Input
          value={detail}
          placeholder="Detailtext"
          onChange={(e) => setDetail(e.value)}
          onBlur={() => updateRow(dataIndex, { DetailText: detail })}
          style={{ width: "100%", borderRadius: 0, marginBottom: "15px" }}
        />
      </td>
    );
  },
  (p, n) =>
    p.dataItem.Beschreibung === n.dataItem.Beschreibung &&
    p.dataItem.DetailText   === n.dataItem.DetailText
);

const EditableTextCell = React.memo(
  ({ dataItem, field, dataIndex, updateRow }) => {
    const [localValue, setLocalValue] = useState(dataItem[field] || "");
    useEffect(() => setLocalValue(dataItem[field] || ""), [dataItem, field]);
    return (
      <td>
        <Input
          value={localValue}
          onChange={(e) => setLocalValue(e.value)}
          onBlur={() => updateRow(dataIndex, { [field]: localValue })}
          style={{ width: "100%" }}
        />
      </td>
    );
  },
  (p, n) => p.dataItem[p.field] === n.dataItem[n.field]
);

/* Tasks-Grid ------------------------------------------------------------- */
const TasksGrid = ({ data, setData, unitOptions, onPersist }) => {
  const [sort, setSort] = useState([]);

  const rebuildPos = (arr) => arr.map((t, i) => ({ ...t, Position: i + 1 }));

  const onRowAction = ({ rowIndex, op }) =>
    setData((prevRaw) => {
      let prev = ensureArray(prevRaw);
      if (op === "add")
        prev = [
          ...prev,
          { Position: prev.length + 1, Beschreibung: "", DetailText: "", Einheit: "" },
        ];
      if (op === "remove") prev = rebuildPos(prev.filter((_, i) => i !== rowIndex));
      if (op === "save")   prev[rowIndex] = { ...prev[rowIndex] };       // state already up-to-date
      onPersist(prev, op);
      return prev;
    });

  const updateRow = (i, ch) => setData((p) => p.map((r, idx) => (idx === i ? { ...r, ...ch } : r)));

  return (
    <Grid
      data={orderBy(data, sort)}
      dataItemKey="Position"
      sortable
      sort={sort}
      onSortChange={(e) => setSort(e.sort)}
    >
      <GridToolbar>
        <Button themeColor="primary" onClick={() => onRowAction({ op: "add" })}>
          Eintrag hinzufügen
        </Button>
      </GridToolbar>

      <GridColumn title="Pos." field="Position" width="80px" />
      <GridColumn
        title="Einheit"
        field="Einheit"
        width="120px"
        cell={(p) => (
          <td>
            <DropDownList
              data={unitOptions}
              value={p.dataItem.Einheit}
              onChange={(e) => updateRow(p.dataIndex, { Einheit: e.value })}
              style={{ width: "100%" }}
            />
          </td>
        )}
      />
      <GridColumn
        title="Beschreibung / Detailtext"
        field="Beschreibung"
        cell={(p) => (
          <DescriptionDetailCell
            {...p}
            dataIndex={p.dataIndex}
            updateRow={updateRow}
          />
        )}
      />
      <GridColumn
        title="Befehl"
        width="110px"
        cell={(p) => (
          <td className="k-command-cell">
            <Button
              fillMode="flat"
              onClick={() => onRowAction({ rowIndex: p.dataIndex, op: "save" })}
            >
              <SvgIcon icon={saveIcon} />
            </Button>
            <Button
              fillMode="flat"
              themeColor="base"
              onClick={() => onRowAction({ rowIndex: p.dataIndex, op: "remove" })}
            >
              <SvgIcon icon={trashIcon} />
            </Button>
          </td>
        )}
      />
    </Grid>
  );
};

/* Units-Grid (unverändert) ---------------------------------------------- */
const UnitsGrid = ({ data, setData, onPersist }) => {
  const [sort, setSort] = useState([]);

  const rebuildPos = (arr) => arr.map((u, i) => ({ ...u, Position: i + 1 }));

  const onRowAction = ({ rowIndex, op }) =>
    setData((prevRaw) => {
      let prev = ensureArray(prevRaw);
      if (op === "add") prev = [...prev, { Position: prev.length + 1, Einheit: "" }];
      if (op === "remove") prev = rebuildPos(prev.filter((_, i) => i !== rowIndex));
      if (op === "save")   prev[rowIndex] = { ...prev[rowIndex] };
      onPersist(prev, op);
      return prev;
    });

  const updateRow = (i, ch) => setData((p) => p.map((r, idx) => (idx === i ? { ...r, ...ch } : r)));

  return (
    <Grid
      data={orderBy(data, sort)}
      dataItemKey="Position"
      sortable
      sort={sort}
      onSortChange={(e) => setSort(e.sort)}
    >
      <GridToolbar>
        <Button themeColor="primary" onClick={() => onRowAction({ op: "add" })}>
          Eintrag hinzufügen
        </Button>
      </GridToolbar>

      <GridColumn title="Pos." field="Position" width="80px" />
      <GridColumn
        title="Einheit"
        field="Einheit"
        cell={(p) => (
          <EditableTextCell
            {...p}
            field="Einheit"
            dataIndex={p.dataIndex}
            updateRow={updateRow}
          />
        )}
      />
      <GridColumn
        title="Befehl"
        width="110px"
        cell={(p) => (
          <td className="k-command-cell">
            <Button
              fillMode="flat"
              onClick={() => onRowAction({ rowIndex: p.dataIndex, op: "save" })}
            >
              <SvgIcon icon={saveIcon} />
            </Button>
            <Button
              fillMode="flat"
              themeColor="base"
              onClick={() => onRowAction({ rowIndex: p.dataIndex, op: "remove" })}
            >
              <SvgIcon icon={trashIcon} />
            </Button>
          </td>
        )}
      />
    </Grid>
  );
};

/* Settings-Page ---------------------------------------------------------- */
const SettingsPage = () => {
  const { showNotification } = useContext(AppContext);
  const [tasks, setTasks] = useState([]);
  const [units, setUnits] = useState([]);
  const unitsDDL = units.map((u) => u.Einheit);

  /* Laden ------------------------------------------------------------- */
  const loadAll = useCallback(async () => {
    const [tRes, uRes] = await Promise.all([
      axios.get(`${config.host}/tasks`),
      axios.get(`${config.host}/units`),
    ]);
    setTasks(Array.isArray(tRes.data) ? tRes.data : dictToTaskArray(tRes.data || {}));
    setUnits(Array.isArray(uRes.data) ? uRes.data : dictToUnitArray(uRes.data || {}));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* Persist-Helper ➜ POST + Toasts ------------------------------------ */
  const persistTasks = async (arr, op) => {
    try {
      await axios.post(`${config.host}/tasks`, arr);
      showNotification("success", op === "remove" ? "Eintrag gelöscht." : "Aufgaben gespeichert.");
    } catch (err) {
      console.error(err);
      showNotification("error", "Fehler beim Speichern der Aufgaben.");
    }
  };
  const persistUnits = async (arr, op) => {
    try {
      await axios.post(`${config.host}/units`, arr);
      showNotification("success", op === "remove" ? "Eintrag gelöscht." : "Einheiten gespeichert.");
    } catch (err) {
      console.error(err);
      showNotification("error", "Fehler beim Speichern der Einheiten.");
    }
  };

  return (
    <div id="Settings" className="profile-page main-content">
      <div className="card-container">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 className="card-title">Stammdatenverwaltung</h3>
        </div>

        {/* Aufgaben --------------------------------------------------- */}
        <Card style={{ marginBottom: 40 }}>
          <CardHeader>
            <CardTitle>Aufgaben&nbsp;(Tätigkeiten)</CardTitle>
          </CardHeader>
          <CardBody>
            <TasksGrid
              data={tasks}
              setData={setTasks}
              unitOptions={unitsDDL}
              onPersist={persistTasks}
            />
            <Button themeColor="primary" style={{ marginTop: 16 }} onClick={() => persistTasks(tasks)}>
              Aufgaben speichern
            </Button>
            <Typography.p style={{ marginTop: 35 }}>
              <b>Hinweis:</b><br />
              Bitte zuerst die Einheitenliste ergänzen, bevor an den Tätigkeiten Änderungen vorgenommen werden.
            </Typography.p>
          </CardBody>
        </Card>

        {/* Einheiten --------------------------------------------------- */}
        <Card>
          <CardHeader>
            <CardTitle>Einheiten-Liste</CardTitle>
          </CardHeader>
          <CardBody>
            <UnitsGrid data={units} setData={setUnits} onPersist={persistUnits} />
            <Button themeColor="primary" style={{ marginTop: 16 }} onClick={() => persistUnits(units)}>
              Einheiten speichern
            </Button>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
